import React, { useEffect, useRef, useState } from 'react';
import { Star, Plus, ArrowLeftRight, X } from 'lucide-react';
import gsap from 'gsap';
import { getTeamData, removeCharacterFromTeam, addCharacterToTeam } from '../logic/teamLogic';
import HUD from './HUD';
import BottomNav from './BottomNav';
import { getCharactersData } from '../logic/charactersLogic';
import iconMap from '../iconMap';
import { ELEMENTS } from '../data';
import { ASSETS } from '../assetManifest';


function CharacterSelectModal({ isOpen, onClose, onSelect, currentTeam }) {
  const characters = getCharactersData().filter(char => !currentTeam.includes(char.id));
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
      <div className="bg-gray-900 rounded-xl w-[480px] max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h2 className="text-xl font-bold">Select Character</h2>
        </div>
        <div className="grid grid-cols-3 gap-2 p-4 max-h-[60vh] overflow-y-auto">
          {characters.map((character) => (
            <div 
              key={character.id}
              onClick={() => onSelect(character.id)}
              className="relative h-48 rounded-lg overflow-hidden border border-gray-700/50 cursor-pointer hover:border-blue-500/50"
            >
              <img
                src={character.portrait}
                alt={character.name}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute bottom-2 left-2">
                <div className="flex gap-1 mb-1">
                  {[...Array(character.stars)].map((_, i) => (
                    <Star key={i} size={14} fill="currentColor" className="text-yellow-400" />
                  ))}
                </div>
                <h3 className="text-sm font-bold">{character.name}</h3>
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-gray-800">
          <button 
            onClick={onClose}
            className="w-full py-2 bg-gray-800 hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Team({ app }) {
  const cardsRef = useRef(null);
  const [teamData, setTeamData] = useState(getTeamData());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        app.goToMenu();
      }
    };
    window.addEventListener('keydown', handleEsc);

    gsap.set(cardsRef.current.children, {
      position: 'relative',
      y: 100,
      opacity: 0
    });

    gsap.to(cardsRef.current.children, {
      y: 0,
      opacity: 1,
      duration: 0.6,
      ease: "power3.out",
      clearProps: "all"
    });

    const interval = setInterval(() => {
      setTeamData(getTeamData());
    }, 10);

    return () => {
      window.removeEventListener('keydown', handleEsc);
      clearInterval(interval);
    };
  }, []);

  const handleCharacterClick = (characterId) => {
    app.goToCharacter(characterId);
  };

  const handleSwap = (index) => {
    setSelectedSlot(index);
    setModalOpen(true);
  };

  const handleRemove = (index) => {
    removeCharacterFromTeam(index);
  };

  const handleSelectCharacter = (characterId) => {
    addCharacterToTeam(characterId, selectedSlot);
    setModalOpen(false);
    setSelectedSlot(null);
  };

  return (
    <div className="bg-gradient-to-b from-[#1a1a2e] via-[#16213e] to-[#1a1a2e] h-full w-full mx-auto relative overflow-hidden text-white">
      <style>
        {`
          @keyframes pulse-border {
            0%, 100% { border-color: rgba(107, 114, 128, 0.5); }
            50% { border-color: rgba(147, 197, 253, 0.8); }
          }
          @keyframes pulse-content {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 0.7; }
          }
          .pulse-card {
            animation: pulse-border 2s infinite ease-in-out;
          }
          .pulse-content {
            animation: pulse-content 2s infinite ease-in-out;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-[url('/assets/nine-realms.png')] opacity-5 bg-cover bg-center" />
      
      <div className="p-6 flex flex-col h-full relative z-10">
        <div ref={cardsRef} className="grid grid-cols-2 gap-4 overflow-y-auto hide-scrollbar pb-24">
          {teamData.characters.map((character, index) => (
            <div 
              key={index} 
              className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 rounded-xl overflow-hidden border border-gray-700/50 backdrop-blur-sm"
              style={{ 
                transition: 'transform 0.3s ease'
              }}
            >
              <div 
                className="relative h-48 cursor-pointer"
                onClick={() => handleCharacterClick(character.id)}
              >
                <img
                  src={character.portrait}
                  alt={character.name}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent" />
                <div className="absolute bottom-2 left-2">
                  <div className="flex gap-1 mb-1">
                    {[...Array(character.stars)].map((_, i) => (
                      <Star key={i} size={14} fill="currentColor" className="text-yellow-400" />
                    ))}
                  </div>
                  <h3 className="text-lg font-bold" style={{ fontFamily: 'Norse' }}>{character.name}</h3>
                  <div className="flex gap-1 mt-1">
                    {character.elements.map((element, index) => {
                      const ElementIcon = iconMap[element];
                      return (
                        <ElementIcon key={index} size={16} className={ELEMENTS[element].color} />
                      );
                    })}
                  </div>
                </div>
              </div>
              
              <div className="p-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  {character.tags.map((tag, i) => (
                    <span key={i} className={`px-2 py-0.5 rounded-full text-xs bg-${tag.color}-500/30 border border-${tag.color}-400/30 text-${tag.color}-300`}>
                      {tag.text}
                    </span>
                  ))}
                </div>
                
                <div className="space-y-1">
                  {Object.entries(character.stats).slice(0, 3).map(([key, value], i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-400">{key.toUpperCase()}</span>
                      <span className="font-medium">{value}</span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 mt-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSwap(index);
                    }}
                    className="flex-1 flex items-center justify-center gap-1 py-1 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-300"
                  >
                    <ArrowLeftRight size={16} />
                    Swap
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(index);
                    }}
                    className="flex-1 flex items-center justify-center gap-1 py-1 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-300"
                  >
                    <X size={16} />
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
          <div 
            onClick={() => {
              setSelectedSlot(teamData.characters.length);
              setModalOpen(true);
            }}
            className="bg-gray-900/30 rounded-xl border-2 border-dashed border-gray-700/50 backdrop-blur-sm flex flex-col cursor-pointer hover:bg-gray-900/50 transition-colors pulse-card"
            style={{ 
              transition: 'transform 0.3s ease'
            }}
          >
            <div className="relative h-48 flex items-center justify-center pulse-content">
              <Plus size={40} className="text-gray-500" />
            </div>
            <div className="p-3">
              <div className="h-[26px] mb-3"></div>
              <div className="space-y-1">
                <div className="h-[20px]"></div>
                <div className="h-[20px]"></div>
                <div className="h-[20px]"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CharacterSelectModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedSlot(null);
        }}
        onSelect={handleSelectCharacter}
        currentTeam={teamData.characters.map(c => c.id)}
      />
    </div>
  );
}

export default Team;
